/* eslint-disable react/no-multi-comp */
import cx from 'classnames';
import React, { useCallback, useMemo } from 'react';
import TreeView, {
    flattenTree,
    INode,
    INodeRendererProps,
    ITreeViewProps,
    ITreeViewOnSelectProps,
} from 'react-accessible-treeview';
import { DiCss3, DiHtml5, DiPython } from 'react-icons/di';
import { FaFolderOpen, FaRegFolder, FaRegImage, FaRegImages, FaUpload } from 'react-icons/fa';

import { NOTIFICATION_THEMES } from '../../../components/constants';
import { showIntlMessageNotification } from '../../notification/actions';
import { SimpleText } from '../../simple-text/simple-text';
import Spin from '../../spin/spin';
import { FILE_UPLOAD_LIMIT_BYTES } from '../constants';
import { useWebservicePropsSelector } from '../context';
import { useUploadImageMutation, useWebserviceProjectQuery } from '../queries';

import { getImageName, getParentIds, getPathToNode, getTree } from './utils';
// @ts-ignore
import styles from './webservice-problem__tree.module.scss';

interface Props {
    onChange: (value: string) => void;
}

interface TreeContext {
    data: INode[];
}

const treeContext = React.createContext<TreeContext>({ data: [] });
const TreeContextProvider = treeContext.Provider;
const useTreeContext = () => React.useContext(treeContext);

const getPaddingLeft = (level: number) => 8 + 16 * (level - 1);

const UploadImageButton = () => {
    const isReadOnly = useWebservicePropsSelector((p) => p.readOnly === true);
    const projectMarkup = useWebservicePropsSelector((p) => p.problem.markup);
    const dispatch = useWebservicePropsSelector((p) => p.dispatch);
    const projectFiles = useWebserviceProjectQuery(projectMarkup);
    const [uploadImage, uploadState] = useUploadImageMutation();

    const fileInputChangeHandler: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) {
                return;
            }
            if (file.size > FILE_UPLOAD_LIMIT_BYTES) {
                const sizeInMb = Math.floor(FILE_UPLOAD_LIMIT_BYTES / 1024 / 1024);
                dispatch?.(
                    showIntlMessageNotification({
                        messageId: 'problemWebservice.uploadTooLarge',
                        messageValues: { size: `${sizeInMb}Mb` },
                        theme: NOTIFICATION_THEMES.FAIL,
                    })
                );
                return;
            }
            const fileName = getImageName(file, projectFiles.data?.images || []);
            await uploadImage([file, fileName, projectMarkup.project_name]);
        },
        [projectMarkup.project_name, projectFiles.data?.images, uploadImage, dispatch]
    );

    if (isReadOnly) {
        return null;
    }
    return (
        <label className={cx('tree-node', styles.upload)} style={{ paddingLeft: getPaddingLeft(2) }}>
            {uploadState.isLoading ? (
                <Spin />
            ) : (
                <>
                    <FaUpload className={styles.icon} style={{ opacity: 0.5, transform: 'scale(0.8)' }} />
                    <SimpleText id="problemWebservice.upload" />
                </>
            )}
            <input accept=".jpg,.jpeg,.png,.gif" onChange={fileInputChangeHandler} type="file" />
        </label>
    );
};

const FolderIcon = ({ isOpen }: { isOpen: boolean }) =>
    isOpen ? <FaFolderOpen className={styles.icon} /> : <FaRegFolder className={styles.icon} />;

const imagesIcon = <FaRegImages className={styles.icon} style={{ opacity: 0.75 }} />;
const imageIcon = <FaRegImage className={styles.icon} style={{ opacity: 0.75 }} />;

const icons = {
    css: <DiCss3 className={styles.icon} style={{ color: '#3498db' }} />,
    html: <DiHtml5 className={styles.icon} style={{ color: '#e74c3c' }} />,
    py: <DiPython className={styles.icon} style={{ color: '#f1c40f', transform: 'scale(1.2)' }} />,
    jpg: imageIcon,
    jpeg: imageIcon,
    png: imageIcon,
    gif: imageIcon,
};

export const FileIcon = ({ filename }: { filename: string }) => {
    const extension = useMemo(() => filename.split('.').pop() as keyof typeof icons, [filename]);
    return (extension && icons[extension]) || null;
};

const TreeNode = (props: INodeRendererProps) => {
    const { element, isBranch, isExpanded, getNodeProps, level } = props;
    // dispatch();
    const data = useTreeContext().data;
    const path = useMemo(() => getPathToNode(data, element.id), [element.id, data]);
    const nodeProps = getNodeProps();
    if (path === 'images/UPLOAD_BUTTON') {
        return <UploadImageButton />;
    }
    const getIcon = () => {
        if (path === 'images') {
            return imagesIcon;
        }
        if (path.startsWith('images/')) {
            return imageIcon;
        }
        if (isBranch) {
            return <FolderIcon isOpen={isExpanded} />;
        }
        return <FileIcon filename={element.name} />;
    };
    return (
        <div {...nodeProps} style={{ paddingLeft: getPaddingLeft(level) }}>
            {getIcon()}
            {element.name}
        </div>
    );
};

export const DirectoryTree = ({ onChange }: Props) => {
    const projectMarkup = useWebservicePropsSelector((p) => p.problem.markup);
    const availableFiles = useWebservicePropsSelector((p) => p.problem.markup.available_files);
    const projectFiles = useWebserviceProjectQuery(projectMarkup);
    const files = useMemo(
        () =>
            Object.keys(projectFiles.data?.files || {}).filter((f) => f !== 'index.html' && availableFiles.includes(f)),
        [projectFiles.data?.files, availableFiles]
    );
    const images = useMemo(() => projectFiles.data?.images || [], [projectFiles.data?.images]);
    const data = useMemo(
        () => flattenTree(getTree([...files, 'images/UPLOAD_BUTTON', ...images.map((i) => `images/${i.name}`)])),
        [files, images]
    );
    // first leaf
    const selected = useMemo(() => data.find((node) => (node.children?.length || 0) === 0)?.id || 0, [data]);
    const defaultSelectedIds = useMemo(() => [selected], [selected]);
    const expanded = useMemo(() => [...getParentIds(data, selected)], [data, selected]);
    const onSelect: ITreeViewProps['onSelect'] = useCallback(
        (selectEvent: ITreeViewOnSelectProps) => {
            if (selectEvent.isSelected && !selectEvent.isBranch) {
                const selectedPath = getPathToNode(data, selectEvent.element.id);
                if (selectedPath !== 'images/UPLOAD_BUTTON') {
                    onChange(selectedPath);
                }
            }
        },
        [data, onChange]
    );
    const contextValue = useMemo(() => ({ data }), [data]);
    return (
        <TreeContextProvider value={contextValue}>
            <div className={cx(styles.directory)}>
                {projectFiles.data ? (
                    <TreeView
                        aria-label="directory tree"
                        data={data}
                        defaultExpandedIds={expanded}
                        defaultSelectedIds={defaultSelectedIds}
                        nodeRenderer={TreeNode}
                        onSelect={onSelect}
                    />
                ) : (
                    <div>Loading...</div>
                )}
            </div>
        </TreeContextProvider>
    );
};
